import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc, arrayUnion, writeBatch, collection, getDocs, serverTimestamp, deleteField } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { X, UploadCloud, CheckCircle, AlertTriangle, History, Edit, Save, Printer, Scissors } from 'lucide-react';
import ReportFailureModal from './Modals/ReportFailureModal';
import { buildTaskAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../../../utils/taskAssignment';
import { isTwoSided, computeProductionStatus, printedBack as printedBackOf, printedDone, productionResetFields } from '../../../lib/production';
import { recordPrepressAdvance } from './PrepressTab';

// --- Helper function para generar el nombre de archivo/plancha ---
const generateSheetFileName = (sheet, jobData) => {
    if (!jobData || !sheet || !sheet.block || !sheet.sheetNumber) return sheet.hri || 'N/A';
    const jobNumber = jobData.id.replace('J-', '');
    const blockObject = jobData.blocks?.find(b => b.name === sheet.block);
    const blockCode = blockObject?.code || sheet.block.replace(/\s+/g, '_').toUpperCase();
    const baseName = `${jobNumber}-${blockCode}_${String(sheet.sheetNumber).padStart(3, '0')}`;
    if (!sheet.isPrinting) return `${baseName}N`;
    if (sheet.printFaces == 1) return `${baseName}P`;
    if (sheet.printFaces == 2) return `${baseName}A/B`;
    return sheet.hri;
};


// Estados que se pueden fijar manualmente desde el modal.
// "Devuelto a Diseño" NO va aquí: se hace con el botón ⚠ (requiere asignar responsable
// + correo). Los estados de producción (Impresa/Cortada/Producido) salen de marcar
// impresa/cortada, y "Reproducir" lo pone la alerta de falla.
// "Listo para Producción" SOLO se ofrece cuando `allowProductionStatus` (Prepress/Producción):
// desde Diseño NO se puede saltar a Producción — el flujo es Diseño → Prepress → Producción.
const SHEET_STATUS_BASE = ['Listo para Prepress', 'Corregido por Diseño', 'En Hold'];
const sheetStatusOptions = (allowProduction) =>
  allowProduction ? ['Listo para Prepress', 'Corregido por Diseño', 'Listo para Producción', 'En Hold'] : SHEET_STATUS_BASE;

// --- Sub-Componentes del Modal ---

const HistoryLogModal = ({ history, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-[61] flex justify-center items-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center"><h2 className="text-lg font-bold flex items-center gap-2"><History size={20}/> Historial de la Plancha</h2><button onClick={onClose}>&times;</button></div>
            <div className="p-6 space-y-3 overflow-y-auto">
                {!history || history.length === 0 ? <p>No hay historial para esta plancha.</p> :
                history.slice().reverse().map((log, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-md border text-sm">
                        <p className="font-bold text-gray-800">{log.action}</p>
                        {log.description && <p className="text-gray-600 mt-1">{log.description}</p>}
                        <p className="text-xs text-gray-400 mt-2">{log.timestamp?.toDate ? new Date(log.timestamp.toDate()).toLocaleString() : 'Fecha inválida'} - {log.user}</p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const FileUploadRow = ({ title, onFileSelected, existingFile, uploadProgress }) => {
    const inputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) { onFileSelected(file); }
        e.target.value = null; 
    };
    
    return (
        <tr className="border-b last:border-b-0">
            <td className="p-2 font-semibold w-1/4">{title}</td>
            <td className="p-2">
                <div className="flex items-center justify-between">
                    {uploadProgress === 100 && !existingFile ? (
                        <span className="text-gray-500">Confirmando...</span>
                    ) : existingFile ? (
                        <a href={existingFile.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:underline">
                            <CheckCircle size={16}/> {existingFile.name}
                        </a>
                    ) : (
                        <span className="text-gray-500">Sin archivo</span>
                    )}
                    
                    {uploadProgress > 0 && uploadProgress < 100 ? (
                        <div className="w-1/2 bg-gray-200 rounded-full h-2 relative">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                            <span className="absolute -top-5 right-0 text-xs text-blue-700">{Math.round(uploadProgress)}%</span>
                        </div>
                    ) : (
                        <button onClick={() => inputRef.current.click()} className="p-2 text-gray-500 hover:text-blue-600" title={existingFile ? "Reemplazar archivo" : "Subir archivo"}>
                            {existingFile ? <Edit size={16}/> : <UploadCloud size={18}/>}
                        </button>
                    )}
                    <input type="file" ref={inputRef} onChange={handleFileChange} className="hidden" />
                </div>
            </td>
        </tr>
    );
};

// ReportFailureModal ahora es compartido (./Modals/ReportFailureModal.js):
// incluye asignación obligatoria a usuario real + sugerencia IA.

// --- Componente Principal del Modal ---
const SheetDetailModal = ({ isOpen, onClose, sheet, materialName, db, storage, jobData, user, allowProductionStatus = false }) => {
    const statusOptions = sheetStatusOptions(allowProductionStatus);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [localSheetData, setLocalSheetData] = useState(sheet);
    const [note, setNote] = useState(sheet?.prepressNotes || '');
    const [isFailureModalOpen, setIsFailureModalOpen] = useState(false);
    const [failureModalType, setFailureModalType] = useState('production'); // 'production' | 'design'
    const [allUsers, setAllUsers] = useState([]);
    const [uploads, setUploads] = useState({});

    useEffect(() => {
        if (sheet) {
            setLocalSheetData(sheet);
            setNote(sheet.prepressNotes || '');
        }
    }, [sheet]);

    // Lista completa de usuarios para la asignación obligatoria de la alerta.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'users'));
                if (!cancelled) setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('Error cargando usuarios para asignación:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [db]);

    const handleFileSelected = (fileKey, file) => {
        let requiredSuffix = '';
        if (fileKey.startsWith('printFile')) requiredSuffix = '_print';
        else if (fileKey.startsWith('cutFiles')) requiredSuffix = '_cut';

        if (requiredSuffix && !file.name.includes(requiredSuffix)) {
            alert(`Nombre de archivo inválido. Debe contener "${requiredSuffix}" para este campo.`);
            return;
        }

        setUploads(prev => ({ ...prev, [fileKey]: { file, progress: 1 } }));
    };
    
    useEffect(() => {
        Object.keys(uploads).forEach(fileKey => {
            const upload = uploads[fileKey];
            if (upload && upload.file && upload.progress === 1) {
                uploadFile(fileKey, upload.file);
            }
        });
    }, [uploads]);

    const uploadFile = (fileKey, file) => {
        const sheetName = generateSheetFileName(localSheetData, jobData);
        const nameParts = file.name.split('.');
        const extension = nameParts.pop();
        const requiredSuffix = fileKey.startsWith('printFile') ? '_print' : '_cut';
        // Distinguir cara: en impresión a 2 caras, Frente = _A y Dorso = _B. Sin esto, frente y
        // dorso generaban el MISMO nombre y colisionaban (el dorso sobrescribía al frente en Storage).
        let sideTag = '';
        if (fileKey === 'printFileFront' && Number(localSheetData.printFaces) === 2) sideTag = '_A';
        else if (fileKey === 'printFileBack') sideTag = '_B';

        const finalFileName = `${sheetName}${requiredSuffix}${sideTag}_x${localSheetData.repetitions || 1}.${extension}`;

        const storageRef = ref(storage, `jobs/${jobData.id}/production/${finalFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploads(prev => ({ ...prev, [fileKey]: { ...prev[fileKey], progress } }));
            },
            (error) => { console.error("Upload error:", error); },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                const fileInfo = { name: finalFileName, url: downloadURL, uploadedBy: user.email, uploadedAt: serverTimestamp() };

                const sheetRef = doc(db, `jobs/${jobData.id}/sheets`, sheet.id);
                const historyEntry = { action: `Archivo de producción subido: ${fileInfo.name}`, user: user.email, timestamp: new Date() };

                let updateData = { history: arrayUnion(historyEntry) };
                const [fileType, subType] = fileKey.split('_');

                if (fileType === 'printFileFront') {
                    updateData.printFileFront = fileInfo;
                    if (localSheetData.printFile) updateData.printFile = deleteField();
                }
                if (fileType === 'printFileBack') updateData.printFileBack = fileInfo;
                if (fileType === 'cutFiles') updateData[`cutFiles.${subType}`] = fileInfo;

                await updateDoc(sheetRef, updateData);

                // Refresca el estado LOCAL para que la fila muestre el archivo subido al instante
                // (el prop `sheet` no es reactivo) y deja de quedarse en "Confirmando...".
                const localFileInfo = { ...fileInfo, uploadedAt: new Date() };
                setLocalSheetData(prev => {
                    const next = { ...prev };
                    if (fileType === 'printFileFront') { next.printFileFront = localFileInfo; }
                    if (fileType === 'printFileBack') { next.printFileBack = localFileInfo; }
                    if (fileType === 'cutFiles') { next.cutFiles = { ...(prev.cutFiles || {}), [subType]: localFileInfo }; }
                    return next;
                });
                // Limpia el progreso de subida de este archivo (oculta la barra / "Confirmando").
                setUploads(prev => { const n = { ...prev }; delete n[fileKey]; return n; });
            }
        );
    };

    // Cambiar el estado de la plancha manualmente (botón de estados).
    const handleStatusChange = async (newStatus) => {
        if (!newStatus || newStatus === localSheetData.status) return;
        // Confirmación (sobre todo para "Listo para Producción" y "En Hold"): pasa la plancha
        // a la siguiente etapa, conviene confirmarlo. Si cancela, el select vuelve solo (controlado).
        if (!window.confirm(`¿Cambiar el estado de la plancha ${generateSheetFileName(localSheetData, jobData)} a "${newStatus}"?`)) return;
        const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', sheet.id);
        const historyEntry = { action: `Estado cambiado a "${newStatus}"`, user: user.email, timestamp: new Date() };
        await updateDoc(sheetRef, { status: newStatus, history: arrayUnion(historyEntry) });
        setLocalSheetData(prev => ({ ...prev, status: newStatus }));
    };

    const handleSaveNote = async () => {
        if (note.trim() === (localSheetData.prepressNotes || '')) return;
        
        const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', sheet.id);
        const historyEntry = { action: 'Nota de Prepress actualizada', details: `Nota: "${note.trim()}"`, user: user.email, timestamp: new Date() };
        await updateDoc(sheetRef, { prepressNotes: note.trim(), history: arrayUnion(historyEntry) });
        alert("Nota guardada.");
    };
    
    // type: 'print' (frente) | 'printBack' (dorso) | 'cut'. El corte exige impresión completa.
    // B1 (D23): por la callable con source 'prepress' (ver recordPrepressAdvance).
    const handleProductionUpdate = async (type) => {
        const fieldToUpdate = type === 'print' ? 'printedCount' : type === 'printBack' ? 'printedCountBack' : 'cutCount';
        const reps = localSheetData.repetitions || 1;
        const currentCount = localSheetData[fieldToUpdate] || 0;
        if (currentCount >= reps) return;
        const newCount = currentCount + 1;
        try {
            const r = await recordPrepressAdvance(db, { jobId: jobData.id, sheetId: sheet.id, type, delta: newCount - currentCount });
            setLocalSheetData((prev) => ({
                ...prev,
                [fieldToUpdate]: Number.isFinite(r.newCount) ? r.newCount : newCount,
                productionStatus: r.productionStatus !== undefined ? r.productionStatus : computeProductionStatus({ ...prev, [fieldToUpdate]: newCount }),
            }));
        } catch (e) {
            console.error('recordProductionScan (prepress) falló:', e);
            alert(`No se pudo registrar la marca: ${e?.message || 'error'}`);
        }
    };
    
    // Unifica reproducción y devolución a Diseño: asigna a un usuario real,
    // persiste el estado coherente y dispara UN correo al asignado.
    const handleReportFailure = async ({ failureType, failedCount, reason, assignedUser, aiCleanedDescription, aiSuggestedAction }) => {
        const sheetName = generateSheetFileName(localSheetData, jobData);
        const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', sheet.id);
        const batch = writeBatch(db);

        const finalDescription = aiCleanedDescription || reason;
        const isDesign = failureType === 'design';

        const baseTask = {
            jobId: jobData.id,
            sheetId: sheet.id,
            relatedPlateId: sheet.id,
            status: 'pending',
            createdBy: user.email,
            createdByName: user.displayName || user.email,
            createdAt: serverTimestamp(),
            originalDescription: reason,
            aiSuggestedAction: aiSuggestedAction || null,
            ...buildTaskAssignment(assignedUser),
            ...buildAssignedByStamp(user),
        };

        let taskData;
        if (isDesign) {
            batch.update(sheetRef, {
                status: 'Devuelto a Diseño',
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Devuelto a Diseño desde Prepress',
                    details: `Motivo: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = { ...baseTask, type: 'system', title: `Corrección: Plancha ${sheetName}`, description: finalDescription };
        } else {
            // Contadores a cero AQUÍ: el ciclo de reproducción arranca al reportar la falla.
            batch.update(sheetRef, {
                ...productionResetFields(),
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Falla de Producción Reportada',
                    details: `${failedCount} plancha(s) dañada(s). Razón: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = { ...baseTask, type: 'system_reproduction', title: `Reproducción: ${failedCount}x Plancha ${sheetName}`, description: `Se solicitaron ${failedCount} planchas de reposición. Causa: ${finalDescription}` };
        }

        batch.set(doc(collection(db, 'jobs', jobData.id, 'tasks')), taskData);

        const mail = buildAssignmentEmail({
            assignedUser,
            assignedBy: user,
            taskTitle: taskData.title,
            taskDescription: taskData.description,
            jobId: jobData.id,
            suggestedAction: aiSuggestedAction,
        });
        if (mail) {
            batch.set(doc(collection(db, 'mail')), mail);
        }

        await batch.commit();
        onClose();
    };

    if (!isOpen || !localSheetData) return null;

    const sheetDisplayName = generateSheetFileName(localSheetData, jobData);
    const printFileToShow = localSheetData.printFileFront || localSheetData.printFile;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
                <div className="bg-gray-50 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                    <header className="flex justify-between items-center p-4 border-b bg-white">
                        <div>
                            <h2 className="text-xl font-bold">Plancha: {sheetDisplayName}</h2>
                        </div>
                        <div className="flex items-center gap-4">
                             <button onClick={() => setIsHistoryModalOpen(true)} className="text-gray-500 hover:text-blue-600 flex items-center gap-1 text-sm"><History size={16}/> Ver Historial</button>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X /></button>
                        </div>
                    </header>
                    <main className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Columna Izquierda: Información y Controles */}
                        <div className="space-y-6">
                             <div className="p-4 border rounded-lg bg-white">
                                <h3 className="font-bold mb-3 text-lg">Información de la Plancha</h3>
                                <div className="text-sm space-y-2">
                                    <p><strong>Material:</strong> {materialName}</p>
                                    <p><strong>Repeticiones:</strong> {localSheetData.repetitions || 1}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <strong>Estado Actual:</strong>
                                        <span className="font-semibold text-purple-700">{localSheetData.status || '—'}</span>
                                    </div>
                                    <div className="pt-1">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Cambiar estado</label>
                                        <select
                                            value={statusOptions.includes(localSheetData.status) ? localSheetData.status : ''}
                                            onChange={(e) => handleStatusChange(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white"
                                        >
                                            {!statusOptions.includes(localSheetData.status) && (
                                                <option value="">— {localSheetData.status || 'Selecciona un estado'} —</option>
                                            )}
                                            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                        <p className="text-xs text-gray-400 mt-1">Para "Devuelto a Diseño" usa el botón ⚠ (asigna responsable).</p>
                                    </div>
                                    {localSheetData.designFileFront && (
                                        <p><strong>Archivo Maestro{localSheetData.printFaces == 2 ? ' (Frente)' : ''}: </strong>
                                            <a href={localSheetData.designFileFront.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{localSheetData.designFileFront.name}</a>
                                        </p>
                                    )}
                                    {localSheetData.printFaces == 2 && localSheetData.designFileBack && (
                                        <p><strong>Archivo Maestro (Dorso): </strong>
                                            <a href={localSheetData.designFileBack.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{localSheetData.designFileBack.name}</a>
                                        </p>
                                    )}
                                </div>
                             </div>
                             <div className="p-4 border rounded-lg bg-white">
                                <h3 className="font-bold mb-3 text-lg">Control de Producción</h3>
                                <div className="flex items-center justify-around gap-4">
                                    {/* "Impresas" solo aplica a planchas de impresión; en solo corte no se muestra. 2 caras = frente + dorso. */}
                                    {localSheetData.isPrinting && <div className="text-center"><span className="font-bold text-2xl">{localSheetData.printedCount || 0}</span><span className="text-gray-500">/{localSheetData.repetitions}</span><p className="text-xs">{isTwoSided(localSheetData) ? 'Impr. Frente' : 'Impresas'}</p></div>}
                                    {isTwoSided(localSheetData) && <div className="text-center"><span className="font-bold text-2xl">{printedBackOf(localSheetData)}</span><span className="text-gray-500">/{localSheetData.repetitions}</span><p className="text-xs">Impr. Dorso</p></div>}
                                    {localSheetData.hasCut !== false && <div className="text-center"><span className="font-bold text-2xl">{localSheetData.cutCount || 0}</span><span className="text-gray-500">/{localSheetData.repetitions}</span><p className="text-xs">Cortadas</p></div>}
                                    <div className="flex flex-col gap-2">
                                        {localSheetData.isPrinting && <button onClick={() => handleProductionUpdate('print')} disabled={(localSheetData.printedCount || 0) >= localSheetData.repetitions} className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50"><Printer size={16}/> {isTwoSided(localSheetData) ? 'Impresa (frente)' : 'Marcar Impresa'}</button>}
                                        {isTwoSided(localSheetData) && <button onClick={() => handleProductionUpdate('printBack')} disabled={printedBackOf(localSheetData) >= localSheetData.repetitions} className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 disabled:opacity-50"><Printer size={16}/> Impresa (dorso)</button>}
                                        {localSheetData.hasCut !== false && <button onClick={() => handleProductionUpdate('cut')} disabled={(localSheetData.cutCount || 0) >= localSheetData.repetitions || (localSheetData.isPrinting && (localSheetData.cutCount || 0) >= printedDone(localSheetData))} className="flex items-center gap-2 px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 disabled:opacity-50"><Scissors size={16}/> Marcar Cortada</button>}
                                    </div>
                                    {/* Reportar Falla: abre el modal donde se elige Producción o Devolver a Diseño. */}
                                    <button onClick={() => { setFailureModalType('production'); setIsFailureModalOpen(true); }} className="p-3 bg-red-100 text-red-700 rounded-full hover:bg-red-200" title="Reportar falla / Devolver a Diseño"><AlertTriangle size={20}/></button>
                                </div>
                             </div>
                             <div className="p-4 border rounded-lg bg-white">
                                 <h3 className="font-bold mb-2">Notas de Prepress</h3>
                                 <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Añadir notas para producción..." className="w-full p-2 border rounded-md text-sm" rows="4"/>
                                 <button onClick={handleSaveNote} className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white font-bold rounded-md hover:bg-gray-700"><Save size={16}/> Guardar Nota</button>
                             </div>
                        </div>
                        
                        {/* Columna Derecha: Carga de Archivos */}
                        <div className="space-y-6">
                            <div className="p-4 border rounded-lg bg-white">
                                <h3 className="font-bold mb-3">Subir Archivos de Producción</h3>
                                <table className="w-full text-sm">
                                    <tbody>
                                        {localSheetData.isPrinting && (
                                            <>
                                                <FileUploadRow 
                                                    title="Impresión (Frente)" 
                                                    onFileSelected={(file) => handleFileSelected('printFileFront', file)}
                                                    existingFile={printFileToShow} 
                                                    uploadProgress={uploads.printFileFront?.progress || 0}
                                                />
                                                {localSheetData.printFaces == 2 && (
                                                    <FileUploadRow 
                                                        title="Impresión (Dorso)" 
                                                        onFileSelected={(file) => handleFileSelected('printFileBack', file)}
                                                        existingFile={localSheetData.printFileBack} 
                                                        uploadProgress={uploads.printFileBack?.progress || 0}
                                                    />
                                                )}
                                            </>
                                        )}
                                        <tr>
                                            <td className="font-semibold p-2 align-top w-1/4">Corte</td>
                                            <td className="p-2">
                                                 <table className="w-full">
                                                     <tbody>
                                                         <FileUploadRow title="ACM" onFileSelected={(file) => handleFileSelected('cutFiles_acm', file)} existingFile={localSheetData.cutFiles?.acm} uploadProgress={uploads['cutFiles_acm']?.progress || 0} />
                                                         <FileUploadRow title="PDF" onFileSelected={(file) => handleFileSelected('cutFiles_pdf', file)} existingFile={localSheetData.cutFiles?.pdf} uploadProgress={uploads['cutFiles_pdf']?.progress || 0} />
                                                         <FileUploadRow title="DXF" onFileSelected={(file) => handleFileSelected('cutFiles_dxf', file)} existingFile={localSheetData.cutFiles?.dxf} uploadProgress={uploads['cutFiles_dxf']?.progress || 0} />
                                                         <FileUploadRow title="ARD" onFileSelected={(file) => handleFileSelected('cutFiles_ard', file)} existingFile={localSheetData.cutFiles?.ard} uploadProgress={uploads['cutFiles_ard']?.progress || 0} />
                                                     </tbody>
                                                 </table>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </main>
                    <footer className="flex justify-end p-4 border-t bg-gray-100">
                        <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700">
                            Cerrar
                        </button>
                    </footer>
                </div>
            </div>
            {isHistoryModalOpen && <HistoryLogModal history={localSheetData.history || []} onClose={() => setIsHistoryModalOpen(false)} />}
            {isFailureModalOpen && (
                <ReportFailureModal
                    isOpen={isFailureModalOpen}
                    onClose={() => setIsFailureModalOpen(false)}
                    onSubmit={handleReportFailure}
                    sheetName={sheetDisplayName}
                    users={allUsers}
                    initialFailureType={failureModalType}
                    sheetContext={`Material: ${materialName} | Reps: ${localSheetData.repetitions || 1}`}
                />
            )}
        </>
    );
};

export default SheetDetailModal;